import styled from "styled-components";
import { rgba } from "polished";
import React, { useEffect, useRef, useState } from "react";
import { Avatar, EditableAvatar } from "../avatar";
import VerifiedBadge from "../../icons/verified-badge";
import { useEditMode } from "../../contexts/edit";
import EmojiFlag from "../emoji-flag";

declare type ArtistInfoProps = {
  className?: string;
  avatar: string;
  title: string;
  description?: string;
  nationality?: string;
  isVerified?: boolean;
};
export const ArtistInfo = ({
  avatar,
  className,
  title,
  description,
  nationality,
  isVerified,
}: ArtistInfoProps) => {
  const {
    editMode,
    editHandlers: { onBioBlur },
  } = useEditMode();
  const [showMore, setShowMore] = useState(false);

  const bioRef = useRef<HTMLTextAreaElement>(null);
  const [bioText, setBioText] = useState(description ?? "");

  useEffect(() => {
    setBioText(description ?? "");
  }, [description]);

  const maxDescriptionCharacters = 100;

  const hasWhiteSpace = (s: string) => {
    return /\s/g.test(s);
  };

  return (
    <Artist className={className}>
      <div className="avatar-wrapper">
        {editMode ? (
          <EditableAvatar title={title} alt={`${title}-img`} src={avatar} />
        ) : (
          <Avatar title={title} alt={`${title}-img`} src={avatar} />
        )}
        {nationality ? (
          <div className="nationality-flag-wrapper">
            <EmojiFlag countryCode={nationality} />
          </div>
        ) : null}
      </div>
      <div className="title-wrapper">
        <h1>{title}</h1>
        {isVerified && <VerifiedBadge />}
      </div>
      {editMode && (
        <BioWrapper onClick={() => bioRef.current?.focus()}>
          <span className="bio-label">Bio</span>
          <textarea
            ref={bioRef}
            value={bioText}
            onBlur={() => onBioBlur(bioText)}
            onChange={(e) => setBioText(e.target.value)}
            className="bio-text"
          />
        </BioWrapper>
      )}
      {!editMode && description && (
        <>
          <span>
            {description.length > maxDescriptionCharacters && !showMore ? (
              <span
                className={`bio-text ${
                  hasWhiteSpace(description) ? "" : "break"
                }`}
              >
                {description.slice(0, maxDescriptionCharacters)}...
              </span>
            ) : (
              <span
                className={`bio-text ${
                  hasWhiteSpace(description) ? "" : "break"
                }`}
              >
                {description}
              </span>
            )}
            {description.length > maxDescriptionCharacters && (
              <>
                <ReadMore onClick={() => setShowMore(!showMore)}>
                  {showMore ? "Read Less" : "Read More"}
                </ReadMore>
              </>
            )}
          </span>
        </>
      )}
    </Artist>
  );
};

const BioWrapper = styled.div`
  width: 100%;
  padding: 16px;
  border: 1px solid ${({ theme }) => rgba(theme.colors.primary, 0.1)};
  cursor: pointer;

  & > .bio-text {
    height: fit-content;
    font-size: 14px;
    font-weight: 400;
    margin: 0;
    opacity: 0.5;
    color: ${({ theme }) => theme.colors.primary};
    width: 100%;
    max-width: 250px;
    background-color: transparent;
    resize: none;
    field-sizing: content;

    &:focus {
      opacity: 1;
    }
  }
  & > .bio-label {
    font-size: 12px;
    font-weight: 500;
    opacity: 0.2;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Artist = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-direction: column;
  width: 100%;

  @media (max-width: ${({ theme }) => `${theme.breakpoints.tablet}px`}) {
    justify-content: center;
    align-items: center;
  }

  .title-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    margin-block: 32px 12px;
    gap: 4px;
    color: ${({ theme }) => theme.colors.primary};

    > h1 {
      color: ${({ theme }) => theme.colors.primary};
      width: 100%;
      margin: 0;
      text-align: center;
      font-size: 32px;
      font-weight: 800;
      line-height: 100%;
      letter-spacing: -1px;
    }
  }

  > span {
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 145%;

    opacity: 0.5;
    color: ${({ theme }) => theme.colors.primary};
    width: 100%;
  }

  .bio-text {
    &.break {
      word-break: break-all;
    }
  }

  .avatar-wrapper {
    position: relative;
  }

  .nationality-flag-wrapper {
    padding: 8px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.white};
    display: flex;
    justify-content: center;
    align-items: center;
    max-width: 32px;
    max-height: 32px;

    position: absolute;
    bottom: 11px;
    right: 16px;
    /* transform: translate(0, -50%); */
  }
`;

const ReadMore = styled.span`
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  line-height: 145%;
`;
